/*
    This is intended for viewing debug information for search results, used to help
    our backend engineers tune search quality.
    The ES debug info is a tree of objects with approximately this schema:
    {
        description: String, represents a function that was applied to the children of this node
        value: Number or null (maybe something else?), the value of this node
        details: Array of ES debug info objects (the children of the instant node)
    }
*/
import React from 'react';

import {
    formatNumberForDisplay,
    getValueOfFirstNodeWithCheckPassing,
} from './SearchEventCardDebugInfoUtils';
import SearchEventCardDebugRelevanceInfo, {
    checkIfNodeIsRelevanceScore,
} from './SearchEventCardDebugRelevanceInfo';

import './SearchEventCardDebugInfo.scss';

const parseElasticSearchDebugInfo = (debugInfo) => {
    const { esExplainPlan } = debugInfo;

    return {
        finalScore: formatNumberForDisplay(esExplainPlan.value),
        geoBoost: getValueOfFirstNodeWithCheckPassing(
            [esExplainPlan],
            (detail) =>
                detail.description.includes(
                    'Function for field location_lat_lon:',
                ),
        ),
        qualityBoost: getValueOfFirstNodeWithCheckPassing(
            [esExplainPlan],
            (detail) =>
                detail.description.includes(
                    'script score function, computed with script:',
                ) && detail.description.includes('event_quality'),
        ),
        dateBoost: getValueOfFirstNodeWithCheckPassing(
            [esExplainPlan],
            (detail) =>
                detail.description.includes(
                    'script score function, computed with script:',
                ) && detail.description.includes('sorted_start_date_local'),
        ),
        relevanceScore: getValueOfFirstNodeWithCheckPassing(
            [esExplainPlan],
            checkIfNodeIsRelevanceScore,
        ),
    };
};

const ScoreBox = ({ title, score, rawScore, rawScoreA, children }) => (
    <div className="search-event-card-debug-info__score-box eds-l-pad-all-1 eds-l-mar-all-1">
        <div className="eds-text-weight--heavy">{title}</div>
        <span className="eds-text-color--ui-blue eds-text-weight--heavy">
            {/* Put the rawScoreA first because it's the new one and more likely to be used than the old */}
            {formatNumberForDisplay(score)} {rawScoreA ? `(${rawScoreA})` : ''}{' '}
            {rawScore ? `(${rawScore})` : ''}
        </span>
        {children}
    </div>
);

const SearchEventCardDebugInfo = (props) => {
    const {
        finalScore,
        geoBoost,
        qualityBoost,
        dateBoost,
        relevanceScore,
    } = parseElasticSearchDebugInfo(props.debugInfo);

    const rawQualityScore = props.debugInfo.score
        ? formatNumberForDisplay(props.debugInfo.score)
        : null;

    const rawQualityScoreA = props.debugInfo.scoreA
        ? formatNumberForDisplay(props.debugInfo.scoreA)
        : null;

    return (
        <div className="search-event-card-debug-info eds-l-pad-bot-1">
            <div className="search-event-card-debug-info__score-box-row">
                <div className="eds-l-mar-all-1 eds-text-color--ui-orange eds-align--center eds-text-weight--heavy">
                    #{props.position}
                </div>
                <ScoreBox title={'Final score'} score={finalScore} />
                <ScoreBox
                    title={'Quality boost'}
                    score={qualityBoost}
                    rawScore={rawQualityScore}
                    rawScoreA={rawQualityScoreA}
                />
                <ScoreBox title={'Geo boost'} score={geoBoost} />
                {dateBoost && (
                    <ScoreBox title={'Date boost'} score={dateBoost} />
                )}
                {relevanceScore && (
                    <ScoreBox title={'Relevance'} score={relevanceScore} />
                )}
            </div>
            {relevanceScore && (
                <SearchEventCardDebugRelevanceInfo
                    debugInfo={props.debugInfo}
                />
            )}
        </div>
    );
};

export default SearchEventCardDebugInfo;
